
import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { Save, RefreshCw, Plus, Trash } from "lucide-react";
import { foundersData } from "@/data/founders";

interface AboutFormData {
  title_en: string;
  title_ar: string;
  description_en: string;
  description_ar: string;
  mission_en: string;
  mission_ar: string;
  founders_title_en: string;
  founders_title_ar: string;
  founders: Array<{
    id: number;
    name_en: string;
    name_ar: string;
    role_en: string;
    role_ar: string;
    bio_en: string;
    bio_ar: string;
    image: string;
  }>;
}

interface AboutEditorProps {
  onSave: (data: any) => void;
}

const AboutEditor: React.FC<AboutEditorProps> = ({ onSave }) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  
  // Initialize the form with current content from foundersData
  const form = useForm<AboutFormData>({
    defaultValues: {
      title_en: "About Palsustain",
      title_ar: "حول بال سستين",
      description_en: "Palsustain is a Palestinian initiative dedicated to advancing sustainable development goals within Palestine's unique context. We focus on creating sustainable solutions that address the specific challenges faced by Palestinian communities.",
      description_ar: "بال سستين هي مبادرة فلسطينية مكرسة لتعزيز أهداف التنمية المستدامة ضمن السياق الفلسطيني الفريد. نركز على إنشاء حلول مستدامة تعالج التحديات المحددة التي تواجهها المجتمعات الفلسطينية.",
      mission_en: "Our mission is to promote and implement sustainable development practices in Palestine while raising awareness about the interconnection between environmental protection, social equity, and economic growth, all within the context of occupation and limited resources.",
      mission_ar: "مهمتنا هي تعزيز وتنفيذ ممارسات التنمية المستدامة في فلسطين مع زيادة الوعي حول الترابط بين حماية البيئة والإنصاف الاجتماعي والنمو الاقتصادي، كل ذلك في سياق الاحتلال والموارد المحدودة.",
      founders_title_en: "Our Founders",
      founders_title_ar: "المؤسسون",
      founders: foundersData.map(founder => ({
        id: founder.id,
        name_en: founder.name.en,
        name_ar: founder.name.ar,
        role_en: founder.role.en,
        role_ar: founder.role.ar,
        bio_en: founder.bio.en,
        bio_ar: founder.bio.ar,
        image: founder.image
      }))
    },
  });
  
  const onSubmit = (data: AboutFormData) => {
    setIsSaving(true);
    
    // Simulate API call to save data
    setTimeout(() => {
      setIsSaving(false);
      toast({
        title: language === "en" ? "Changes saved" : "تم حفظ التغييرات",
        description: language === "en" ? "About section has been updated" : "تم تحديث قسم حول",
      });
      onSave(data);
    }, 1000);
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {language === "en" ? "English Content" : "المحتوى الإنجليزي"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "About Title (English)" : "عنوان حول (الإنجليزية)"}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="description_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "About Description (English)" : "وصف حول (الإنجليزية)"}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} className="min-h-24" />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="mission_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "Mission Statement (English)" : "بيان المهمة (الإنجليزية)"}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} className="min-h-24" />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="founders_title_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "Founders Title (English)" : "عنوان المؤسسين (الإنجليزية)"}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {language === "en" ? "Arabic Content" : "المحتوى العربي"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title_ar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "About Title (Arabic)" : "عنوان حول (العربية)"}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} dir="rtl" />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="description_ar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "About Description (Arabic)" : "وصف حول (العربية)"}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} dir="rtl" className="min-h-24" />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="mission_ar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "Mission Statement (Arabic)" : "بيان المهمة (العربية)"}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} dir="rtl" className="min-h-24" />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="founders_title_ar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "Founders Title (Arabic)" : "عنوان المؤسسين (العربية)"}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} dir="rtl" />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {language === "en" ? "Founders" : "المؤسسون"}
            </CardTitle>
            <CardDescription>
              {language === "en" 
                ? "Edit the founders information displayed on the about section" 
                : "تحرير معلومات المؤسسين المعروضة في قسم حول"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {form.watch('founders').map((founder, index) => (
                <Card key={founder.id} className="border border-muted">
                  <CardHeader className="py-3 px-4 bg-muted/20 flex flex-row items-center justify-between">
                    <CardTitle className="text-base">
                      {language === "en" ? `Founder #${index + 1}` : `المؤسس #${index + 1}`}
                    </CardTitle>
                    <Button 
                      type="button" 
                      variant="destructive" 
                      size="sm"
                      onClick={() => {
                        const currentFounders = [...form.getValues('founders')];
                        currentFounders.splice(index, 1);
                        form.setValue('founders', currentFounders);
                      }}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name={`founders.${index}.name_en`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {language === "en" ? "Name (English)" : "الاسم (الإنجليزية)"}
                              </FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name={`founders.${index}.role_en`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {language === "en" ? "Role (English)" : "الدور (الإنجليزية)"}
                              </FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name={`founders.${index}.bio_en`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {language === "en" ? "Bio (English)" : "السيرة الذاتية (الإنجليزية)"}
                              </FormLabel>
                              <FormControl>
                                <Textarea {...field} className="min-h-24" />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name={`founders.${index}.name_ar`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {language === "en" ? "Name (Arabic)" : "الاسم (العربية)"}
                              </FormLabel>
                              <FormControl>
                                <Input {...field} dir="rtl" />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name={`founders.${index}.role_ar`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {language === "en" ? "Role (Arabic)" : "الدور (العربية)"}
                              </FormLabel>
                              <FormControl>
                                <Input {...field} dir="rtl" />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name={`founders.${index}.bio_ar`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {language === "en" ? "Bio (Arabic)" : "السيرة الذاتية (العربية)"}
                              </FormLabel>
                              <FormControl>
                                <Textarea {...field} dir="rtl" className="min-h-24" />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <FormField
                        control={form.control}
                        name={`founders.${index}.image`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {language === "en" ? "Profile Image URL" : "رابط صورة الملف الشخصي"}
                            </FormLabel>
                            <FormControl>
                              <div className="space-y-4">
                                <Input {...field} />
                                {field.value && (
                                  <div className="relative w-40 h-40 mx-auto rounded-md overflow-hidden border border-muted">
                                    <img 
                                      src={field.value} 
                                      alt={`Founder ${index + 1}`} 
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.src = "/placeholder.svg";
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              <Button
                type="button"
                variant="outline"
                className="w-full border-dashed border-palsustain-green text-palsustain-green hover:bg-palsustain-green/5"
                onClick={() => {
                  const currentFounders = [...form.getValues('founders')];
                  const newId = currentFounders.length > 0 ? Math.max(...currentFounders.map(f => f.id)) + 1 : 1;
                  
                  currentFounders.push({
                    id: newId,
                    name_en: "",
                    name_ar: "",
                    role_en: "",
                    role_ar: "",
                    bio_en: "",
                    bio_ar: "",
                    image: "/placeholder.svg"
                  });
                  
                  form.setValue('founders', currentFounders);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                {language === "en" ? "Add Founder" : "إضافة مؤسس"}
              </Button>
            </div>
          </CardContent>
        </Card>
      
        <div className="flex justify-end gap-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => form.reset()}
            className="border-palsustain-green text-palsustain-green"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {language === "en" ? "Reset" : "إعادة تعيين"}
          </Button>
          <Button 
            type="submit" 
            className="bg-palsustain-green hover:bg-palsustain-green/90"
            disabled={isSaving}
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving 
              ? (language === "en" ? "Saving..." : "جاري الحفظ...") 
              : (language === "en" ? "Save Changes" : "حفظ التغييرات")
            }
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AboutEditor;
